// @ts-check
// Entrega de una traducción en el editor de destino (DEC-107).
//
// La traducción se deja en el AUTOGUARDADO del editor de destino y luego se
// navega a su página. No hay ningún canal nuevo: es exactamente el mismo sitio
// del que cada editor se recupera al abrirse, así que abrir la traducción es
// indistinguible de haberla escrito allí.
//
// ## Por qué no se pasa por la URL
//
// Un programa entero no cabe cómodamente en una dirección, quedaría en el
// historial del navegador y se vería en la barra. El almacenamiento local ya
// existe, ya está aislado por usuario donde importa
// (`diagram-editor/editor-persistence.js`) y ya se limpia al cerrar sesión
// (`state/editor-drafts.js`).

import { crearEstadoVisual } from '../diagram-editor/editor-visual-state.js';
import { deserializarPrograma } from '../diagram-editor/ir-serialization.js';
import { guardarProgramaLocal } from '../diagram-editor/editor-persistence.js';
import { crearListaAlgoritmos as crearListaPseudo } from '../pseudocode-editor/pseudo-model.js';
import { guardarEstadoPseudocodigoLocal } from '../pseudocode-editor/pseudocode-persistence.js';
import { crearListaAlgoritmos as crearListaPython } from '../python-editor/py-model.js';
import { guardarEstadoPythonLocal } from '../python-editor/python-persistence.js';
import { PAGINA_REPRESENTACION } from './index.js';
import { AMBITO_LIBRE } from '../../state/ambito-borrador.js';
import { olvidarEjercicio } from '../../state/ejercicio-seleccionado.js';

/** @typedef {import('./index.js').Representacion} Representacion */

/**
 * Deja la traducción lista en el editor de destino.
 *
 * Devuelve `false` si no se ha podido guardar (almacenamiento lleno o no
 * disponible): en ese caso NO se debe navegar, o el alumno llegaría al otro
 * editor y encontraría lo de antes sin entender por qué.
 *
 * @param {Representacion} hacia
 * @param {any} resultado Texto para pseudocódigo/Python; IR-JSON para diagrama.
 * @param {{ usuarioId?: string | null }} [opciones]
 * @returns {boolean}
 */
export function dejarEnEditor(hacia, resultado, opciones = {}) {
  try {
    if (hacia === 'pseudocodigo') {
      // `crearListaAlgoritmos` devuelve la lista; el activo es el principal,
      // que es el único que existe en una traducción recién hecha.
      const algoritmos = crearListaPseudo({ textoPrincipal: String(resultado) });
      return guardarEstadoPseudocodigoLocal(algoritmos, algoritmos[0].id, AMBITO_LIBRE).ok === true;
    }
    if (hacia === 'python') {
      const algoritmos = crearListaPython({ textoPrincipal: String(resultado) });
      return guardarEstadoPythonLocal(algoritmos, algoritmos[0].id, AMBITO_LIBRE).ok === true;
    }
    if (hacia === 'diagrama') {
      // El editor NO guarda IR-JSON crudo: guarda su MODELO, que es la misma
      // forma más un `id` en cada nodo (ver `diagram-editor/ir-serialization.js`).
      // Sin esta conversión el diagrama se guarda con nodos sin identificar y
      // el editor no puede dibujarlo ni ejecutarlo.
      const modelo = deserializarPrograma(resultado);
      // Sin estado visual: el lienzo coloca los bloques a partir de la
      // estructura del programa, no de posiciones guardadas, así que un
      // diagrama traducido se dibuja igual que uno recién creado.
      const guardado = guardarProgramaLocal(
        modelo,
        crearEstadoVisual(),
        opciones.usuarioId ?? undefined,
        AMBITO_LIBRE,
      );
      return guardado.ok === true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Navega a la página del editor de destino.
 * @param {Representacion} hacia
 * @param {{ navegar?: (url: string) => void }} [opciones]
 */
export function abrirEditor(hacia, opciones = {}) {
  // Quien acaba de traducir no está haciendo ningún ejercicio: lo que va a
  // ver es su programa convertido, no un intento. Si el editor de destino
  // siguiera recordando el último ejercicio, se abriría enseñando un
  // enunciado que no tiene nada que ver con lo que hay en pantalla -- y
  // cargaría el borrador de ESE ejercicio en vez de la traducción, que se
  // guarda en el borrador libre (ver `dejarEnEditor`). Petición del usuario
  // (2026-08-06): "deberia volver el selector de ejercicio a elige ejercicio,
  // dado que no estoy haciendo ningun ejercicio".
  olvidarEjercicio(hacia);
  const url = PAGINA_REPRESENTACION[hacia];
  if (opciones.navegar) {
    opciones.navegar(url);
    return;
  }
  globalThis.location.assign(url);
}
